import {computed, effectScope, watch} from 'vue'
import {ThemeType} from '@toolify/server/src/services/UserSettingsService/strategies/ThemeUserSettingStrategy/enum/ThemeType'
import {
  resolveThemeType,
} from '@toolify/server/src/services/UserSettingsService/strategies/ThemeUserSettingStrategy/functions/resolveThemeType'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'
import {IUserResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IUserResponseInstance'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {useGuestAppearance} from '@toolify/client/src/modules/authentication/composables/useGuestAppearance'
import {readRememberedThemeType} from '@toolify/client/src/modules/theme/functions/themeBootState'
import {hasExplicitGuestTheme} from '@toolify/client/src/modules/theme/functions/hasExplicitGuestTheme'

// Zapamietany motyw light obowiazuje tylko do pierwszego pobrania uzytkownika/goscia. Potem (wylogowanie,
// nieudana sesja goscia) bez uzytkownika obowiazuje motyw goscia, jak w Fiord Night/Dark dotad
let hasResolvedEntity = false
let isResolvedEntityWatched = false

function watchResolvedEntity(authStore: ReturnType<typeof useAuthStore>): void {
  if (isResolvedEntityWatched) {
    return
  }
  isResolvedEntityWatched = true
  effectScope(true).run(() => {
    watch(() => authStore.currentEntity, currentEntity => {
      if (currentEntity) {
        hasResolvedEntity = true
      }
    }, {
      immediate: true,
      flush: 'sync',
    })
  })
}

// Jedyne miejsce, ktore decyduje o aktywnym motywie. `data-theme` (App.vue) i zmienne JS
// (useThemeVariables, canvas tabeli) musza czytac to samo - liczone osobno rozjezdzaly sie
// na dwa rozne motywy naraz.
export function useCurrentThemeType() {
  const authStore = useAuthStore()
  const {theme: guestTheme} = useGuestAppearance()
  watchResolvedEntity(authStore)

  const currentThemeType = computed<ThemeType>(() => {
    // odczyt przed wczesnym returnem: computed ma zawsze sledzic motyw goscia
    const resolvedGuestTheme = guestTheme.value
    if (!authStore.currentEntity && !hasResolvedEntity && !hasExplicitGuestTheme()) {
      // przed pierwszym pobraniem uzytkownika/goscia: motyw light z poprzedniego uruchomienia (themeBootState) zamiast
      // domyslnego Fiord Night - bez tego start w light mrugal ciemnym ekranem. Fiord Night/Dark nie sa zapamietywane,
      // a jawny wybor goscia (?t=…, zapisany motyw goscia) wygrywa jak dotad
      const rememberedThemeType = readRememberedThemeType()
      if (rememberedThemeType) {
        return rememberedThemeType
      }
    }
    const user = authStore.currentEntity?.entityType === EntityType.User
      ? authStore.currentEntity.payload as IUserResponseInstance
      : null
    return resolveThemeType(user
      ? user.settings?.theme
      : resolvedGuestTheme)
  })

  return {
    currentThemeType,
  }
}
